import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Label,
  Avatar,
  Icon,
  Badge,
  Autocomplete,
  AutocompleteItem,
  Chip,
} from 'mw-style-react';
import cn from 'classnames';
import useScroll from 'useScroll';
import useOutsideClick from 'useOutsideClick';
import useIntl from 'useIntl';
import AppUtils from '@control-front-end/utils/utils';
import mes from './intl';
import './SelectEntityField.scss';

/**
 * Поле поиска слоев/акторов/справочников/шаблонов/скриптов с поиском
 */
function SelectEntityField(props) {
  const {
    objType,
    id,
    value,
    error,
    placeholder,
    helperText,
    bordered,
    isDisabled,
    onChange,
    onSearch,
    onBlur,
    onFocus,
    onScroll,
    size = 'medium',
    type = 'default',
    list = [],
    multiselect = false,
    label,
    autoFocus,
    resultLabel,
    wrap = false,
  } = props;
  const t = useIntl();
  const searchBox = useRef();
  const [query, setQuery] = useState('');
  const [focus, setFocus] = useState(false);

  const clearSearch = () => {
    setQuery('');
    setFocus(false);
  };

  useOutsideClick({ ref: searchBox, callback: clearSearch });
  useScroll({ ref: searchBox, callback: onScroll });

  /**
   * Обработка поискового запроса
   * @param q
   */
  const handleChange = ({ value: q }) => {
    setQuery(q);
    onSearch(q);
  };

  /**
   * Обработка снятия фокуса
   */
  const handleBlur = () => {
    setFocus(false);
    setQuery('');
    if (!onBlur) return;
    onBlur();
  };

  /**
   * Удалить элемент
   */
  const handleDelItem = (itemId) => {
    const newVal = AppUtils.removeFromArrayObject(value, 'id', itemId);
    onChange({ id, value: newVal });
    handleBlur();
  };

  /**
   * Обработка клика по найденному варианту
   * @param item
   */
  const handleItemClick = (item) => {
    const newItem = {
      id: item.id,
      title: item.title || item.name,
      createdAt: item.createdAt,
      ownerId: item.user ? item.user.id : item.ownerId,
      ownerName: item.user ? item.user.nick : item.ownerName,
      ownerAvatar: item.user ? item.user.avatar : item.ownerAvatar,
      pictureUrl: item.pictureUrl,
    };
    let val;
    if (multiselect) {
      val = value.slice();
      val.push(newItem);
    } else {
      val = newItem;
    }
    onChange({ id, value: val });
    handleBlur();
  };

  /**
   * Обработка фокуса
   */
  const handleFocus = () => {
    setFocus(true);
    if (!onFocus) return;
    onFocus();
  };

  const emptyResult = () => (
    <AutocompleteItem
      id="not_found"
      size="small"
      value="Not found"
      onClick={handleBlur}
    />
  );

  const renderItemCustomEl = (item) => {
    switch (objType) {
      case 'actor':
        return (
          <div styleName="se__item__avatar__box">
            <Avatar styleName="se__item__avatar" src={item.pictureUrl}>
              {!item.pictureUrl ? <Icon type="actor" size="small" /> : null}
            </Avatar>
          </div>
        );
      case 'layer':
        return (
          <div styleName="se__item__avatar__box">
            <Avatar styleName="se__item__avatar">
              <Icon type="layer" size="small" />
            </Avatar>
          </div>
        );
      case 'script':
        return (
          <div styleName="se__item__avatar__box">
            <Avatar styleName="se__item__avatar">
              <Icon type="form" size="small" />
            </Avatar>
          </div>
        );
      case 'template':
        return (
          <Badge
            styleName="se__item__badge"
            bgColor={item.color || '#ffffff'}
            borderColor={item.color || '#ACB3BE'}
          />
        );
      default:
        return null;
    }
  };

  const renderSearchResult = () => {
    if (!focus) return;
    const items = list.map((i) => (
      <AutocompleteItem
        styleName="se__item"
        key={i.id}
        id={i.id}
        value={i.name || i.title}
        onClick={() => handleItemClick(i)}
        children={renderItemCustomEl(i)}
      />
    ));
    if (!query.length && !list.length) return;
    return <div>{list.length ? items : emptyResult()}</div>;
  };

  const renderChipsItems = () => {
    if (!multiselect) {
      return () => {};
    }
    return value.map((item) => (
      <Chip
        key={item.id}
        label={item.title || item.name}
        size="small"
        type="rectangular"
        fontWeight="semibold"
        bgColor="#F0F6FD"
        borderColor="#BAD5F8"
        closeClick={() => handleDelItem(item.id)}
      />
    ));
  };

  const renderSearchTitle = () => {
    const defL = resultLabel || t(mes.recent);
    const sLabel = query.length > 2 ? t(mes.searchResults) : defL;
    return <Label styleName="se__title" fontWeight="semibold" value={sLabel} />;
  };

  const getValueTitle = () => {
    if (!value) return '';
    return value.name || value.title || '';
  };

  const getPortalOptions = () => {
    if (type !== 'modal') return null;
    let offsetY;
    if (size === 'large') {
      offsetY = 64;
    } else if (size === 'medium' && bordered) {
      offsetY = 40;
    } else {
      offsetY = 52;
    }
    return { offsetY };
  };

  const searchField = (
    <div ref={searchBox}>
      <Autocomplete
        styleName={cn('se__field', type)}
        size={size}
        placeholder={placeholder}
        label={label}
        value={focus ? query : getValueTitle()}
        bordered={bordered}
        error={error}
        helperText={helperText}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        visibility={isDisabled ? 'disabled' : 'visible'}
        searchResult={renderSearchResult}
        searchTitle={renderSearchTitle}
        chipsItems={renderChipsItems}
        portalOptions={getPortalOptions()}
        autoFocus={autoFocus}
      />
    </div>
  );

  return wrap ? <div styleName="se__wrap">{searchField}</div> : searchField;
}

SelectEntityField.propTypes = {
  id: PropTypes.string,
  objType: PropTypes.oneOf([
    'layer',
    'actor',
    'template',
    'refBook',
    'actorFilter',
    'script',
  ]),
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  type: PropTypes.oneOf(['default', 'modal']),
  list: PropTypes.array,
  label: PropTypes.string,
  wrap: PropTypes.bool,
  resultLabel: PropTypes.string,
  error: PropTypes.bool,
  multiselect: PropTypes.bool,
  bordered: PropTypes.bool,
  isDisabled: PropTypes.bool,
  autoFocus: PropTypes.bool,
  helperText: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  onSearch: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onScroll: PropTypes.func,
};

export default SelectEntityField;
